import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Me } from '@pcp/contracts';
import { ApiRequestError } from '../api/client';
import { isTwoFactorChallenge, useHealth, useLogin, useVerifyTwoFactor } from '../api/queries';
import { FORCED_PASSWORD_ROUTE, homeFor } from '../auth/guards';
import { HealthBadge } from '../components/HealthBadge';
import { t } from '../i18n/pt-BR';

const messageOf = (error: unknown) =>
  error instanceof ApiRequestError ? error.message : error ? t.states.datasulDown : null;

export function LoginPage() {
  const login = useLogin();
  const verify = useVerifyTwoFactor();
  const navigate = useNavigate();
  const location = useLocation() as { state: { from?: string } | null };
  const health = useHealth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  const enter = (me: Me) => {
    const from = location.state?.from;
    // O operador nunca aterrissa em tela de PCP; quem precisa ativar o 2FA vai direto para a segurança
    const target = me['troca-senha-obrigatoria']
      ? FORCED_PASSWORD_ROUTE
      : me['dois-fatores']['exige-configuracao']
        ? '/conta/seguranca'
        : me.perfil === 'operator' || !from || from === '/login'
          ? homeFor(me.perfil)
          : from;
    navigate(target, { replace: true });
  };

  const onPassword = (e: FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email, senha: password },
      {
        onSuccess: (result) => {
          if (isTwoFactorChallenge(result)) setNeedsCode(true);
          else enter(result);
        },
      },
    );
  };

  const onCode = (e: FormEvent) => {
    e.preventDefault();
    verify.mutate({ codigo: code, 'confiar-dispositivo': trustDevice }, { onSuccess: enter });
  };

  const backToPassword = () => {
    setNeedsCode(false);
    setCode('');
    setPassword('');
    verify.reset();
    login.reset();
  };

  // como no ROI, o cartão balança quando a senha ou o código é recusado
  const failed = !!login.error || !!verify.error;
  const shake = failed;

  // O desafio expira (5 min) ou esgota as tentativas: volta para a senha com a mensagem da API
  const verifyError = verify.error;
  const challengeGone =
    verifyError instanceof ApiRequestError &&
    verifyError.status === 401 &&
    /expirou|tentativas/.test(verifyError.message);

  return (
    <div className="login">
      <div className={`login-card${shake ? ' shake' : ''}`}>
        <img className="logo" src="/acg-logo.png" alt={t.app.logoAlt} width={120} height={68} />
        <span className="login-badge">{t.auth.badge}</span>
        <h1 className="login-title">{t.auth.heading}</h1>
        <p className="login-sub">{needsCode ? t.auth.twoFactor.title : t.auth.tagline}</p>

        {!needsCode ? (
          <form onSubmit={onPassword} noValidate>
            <div>
              <label className="sr-only" htmlFor="email">
                {t.auth.email}
              </label>
              <input
                id="email"
                className="login-input"
                type="email"
                autoComplete="username"
                autoFocus
                required
                placeholder={t.auth.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="sr-only" htmlFor="password">
                {t.auth.password}
              </label>
              <input
                id="password"
                className="login-input"
                type="password"
                autoComplete="current-password"
                required
                placeholder={t.auth.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="login-btn" disabled={login.isPending || !email || !password}>
              {login.isPending ? t.auth.submitting : t.auth.submit}
            </button>
            <p className="login-error" role={messageOf(login.error) ? 'alert' : undefined}>
              {messageOf(login.error)}
            </p>
          </form>
        ) : (
          <form onSubmit={onCode} noValidate>
            <p className="hint" style={{ textAlign: 'center' }}>
              {useRecovery ? t.auth.twoFactor.recoverySubtitle : t.auth.twoFactor.subtitle}
            </p>
            <div>
              <label className="sr-only" htmlFor="code">
                {useRecovery ? t.auth.twoFactor.recoveryCode : t.auth.twoFactor.code}
              </label>
              <input
                id="code"
                className="login-input mono"
                inputMode={useRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
                required
                maxLength={useRecovery ? 20 : 7}
                placeholder={useRecovery ? 'XXXXX-XXXXX' : '000000'}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{ letterSpacing: useRecovery ? 1 : 6, fontSize: 18 }}
              />
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              <span>
                {t.auth.twoFactor.trust}
                <span className="hint" style={{ display: 'block' }}>
                  {t.auth.twoFactor.trustHint}
                </span>
              </span>
            </label>
            {challengeGone ? (
              <button type="button" className="login-btn" onClick={backToPassword}>
                {t.auth.twoFactor.back}
              </button>
            ) : (
              <button
                type="submit"
                className="login-btn"
                disabled={verify.isPending || code.trim().length < 6}
              >
                {verify.isPending ? t.auth.twoFactor.verifying : t.auth.twoFactor.verify}
              </button>
            )}
            <p className="login-error" role={messageOf(verifyError) ? 'alert' : undefined}>
              {messageOf(verifyError)}
            </p>
            <div className="login-links">
              <button
                type="button"
                className="link"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                  verify.reset();
                }}
              >
                {useRecovery ? t.auth.twoFactor.useApp : t.auth.twoFactor.useRecovery}
              </button>
              <button type="button" className="link" onClick={backToPassword}>
                {t.auth.twoFactor.back}
              </button>
            </div>
          </form>
        )}

        {health.data?.datasul === 'mock' ? <p className="login-meta">{t.auth.demoHint}</p> : null}
        <p className="login-meta">
          <HealthBadge />
        </p>
        <span className="login-foot">{t.auth.footer}</span>
      </div>
    </div>
  );
}
